// CustomFieldDependenciesEditor — list view of custom-field dependency rules.
//
// Design (spec §4.4): List grouped by "when" field. Each rule: "When Field A =
// Value X, Then Field B is restricted to listed Values." Add/remove rules.
//
// Add-rule form is rendered inline (below the list) using ZodForm + Form*
// wrappers. The form is shown/hidden by a toggle. It validates that the "when"
// and "then" fields are different before calling onCreate.
//
// Dependency shape matches the backend POST body from
// apps/api/src/routes/conversations/admin/custom-field-dependencies.ts:
//   whenFieldDefinitionId, whenValueId, thenFieldDefinitionId, thenValueIds
//
// FieldDefinition and FieldValue shapes derived from the admin route files and
// the existing CustomFieldValuesTree/Editor convention (id + label).

import { useState, useEffect, type JSX } from 'react'
import { z } from 'zod'
import { useFormContext } from 'react-hook-form'
import {
  ZodForm,
  FormDropdown,
  FormMultiSelect,
} from '../../forms/index.js'
import { useToast } from '../../system/ToastProvider.js'

// ---------------------------------------------------------------------------
// Public types — match backend shapes exactly
// ---------------------------------------------------------------------------

export interface Dependency {
  id: string
  whenFieldDefinitionId: string
  whenValueId: string
  thenFieldDefinitionId: string
  thenValueIds: string[]
}

export interface FieldDefinition {
  id: string
  fieldKey: string
  label: string
}

export interface FieldValue {
  id: string
  label: string
  fieldDefinitionId: string
}

export interface CustomFieldDependenciesEditorProps {
  rules: Dependency[]
  fields: FieldDefinition[]
  valuesByField: Record<string, FieldValue[]>
  onCreate: (input: Omit<Dependency, 'id'>) => Promise<void>
  onDelete: (id: string) => Promise<void>
}

// ---------------------------------------------------------------------------
// Zod schema for the add-rule form
// ---------------------------------------------------------------------------

const AddRuleSchema = z.object({
  whenFieldDefinitionId: z.string().min(1, 'Select a "when" field'),
  whenValueId: z.string().min(1, 'Select a "when" value'),
  thenFieldDefinitionId: z.string().min(1, 'Select a "then" field'),
  thenValueIds: z.array(z.string()).min(1, 'Select at least one "then" value'),
})

type AddRuleFormData = z.infer<typeof AddRuleSchema>

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Group rules by their whenFieldDefinitionId. Returns entries in insertion order. */
function groupByWhenField(
  rules: Dependency[],
): Array<{ fieldId: string; rules: Dependency[] }> {
  const order: string[] = []
  const map: Record<string, Dependency[]> = {}
  for (const rule of rules) {
    const key = rule.whenFieldDefinitionId
    if (!map[key]) {
      order.push(key)
      map[key] = []
    }
    map[key]!.push(rule)
  }
  return order.map((fieldId) => ({ fieldId, rules: map[fieldId]! }))
}

function labelFor(items: Array<{ id: string; label: string }>, id: string): string {
  return items.find((x) => x.id === id)?.label ?? id
}

// ---------------------------------------------------------------------------
// AddRuleForm — inline form, extracted for clarity
// ---------------------------------------------------------------------------

interface AddRuleFormProps {
  fields: FieldDefinition[]
  valuesByField: Record<string, FieldValue[]>
  onSave: (data: AddRuleFormData) => Promise<void>
  onCancel: () => void
}

// AddRuleFormBody is a real component (not just a render callback) so that
// useEffect is legal here. It lives inside the FormProvider supplied by ZodForm.
interface AddRuleFormBodyProps {
  fieldOptions: Array<{ label: string; value: string }>
  valuesByField: Record<string, FieldValue[]>
  onCancel: () => void
}

function AddRuleFormBody({
  fieldOptions,
  valuesByField,
  onCancel,
}: AddRuleFormBodyProps): JSX.Element {
  const methods = useFormContext<AddRuleFormData>()
  const whenFieldId = methods.watch('whenFieldDefinitionId') as string
  const thenFieldId = methods.watch('thenFieldDefinitionId') as string

  // Reset whenValueId whenever the "When Field" changes so a stale value from
  // a previous field cannot survive and be submitted.
  useEffect(() => {
    methods.setValue('whenValueId', '')
  }, [whenFieldId]) // methods.setValue is stable (react-hook-form guarantee); only whenFieldId needs to be in deps

  // Reset thenValueIds whenever the "Then Field" changes for the same reason.
  useEffect(() => {
    methods.setValue('thenValueIds', [])
  }, [thenFieldId]) // methods.setValue is stable (react-hook-form guarantee); only thenFieldId needs to be in deps

  const whenValueOptions = (valuesByField[whenFieldId] ?? []).map((v) => ({
    label: v.label,
    value: v.id,
  }))

  const thenValueOptions = (valuesByField[thenFieldId] ?? []).map((v) => ({
    label: v.label,
    value: v.id,
  }))

  // Cross-field validation: when === then is disallowed
  const sameFieldError =
    whenFieldId && thenFieldId && whenFieldId === thenFieldId
      ? 'When and Then cannot be the same field'
      : null

  return (
    <div>
      <FormDropdown
        name="whenFieldDefinitionId"
        label="When Field"
        options={fieldOptions}
        placeholder="Select field…"
        required
      />

      <FormDropdown
        name="whenValueId"
        label="When Value"
        options={whenValueOptions}
        placeholder={whenFieldId ? 'Select value…' : 'Select a When Field first'}
        disabled={!whenFieldId}
        required
      />

      <FormDropdown
        name="thenFieldDefinitionId"
        label="Then Field"
        options={fieldOptions}
        placeholder="Select field…"
        required
      />

      {sameFieldError && (
        <p className="mb-2 text-sm text-red-600" role="alert">
          {sameFieldError}
        </p>
      )}

      <FormMultiSelect
        name="thenValueIds"
        label="Then Values"
        options={thenValueOptions}
        placeholder={thenFieldId ? 'Select values…' : 'Select a Then Field first'}
        disabled={!thenFieldId || !!sameFieldError}
        required
      />

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="rounded bg-[#16273F] px-3 py-1.5 text-sm text-white hover:bg-[#1D3352] disabled:opacity-50"
          disabled={!!sameFieldError}
        >
          Save
        </button>
      </div>
    </div>
  )
}

function AddRuleForm({ fields, valuesByField, onSave, onCancel }: AddRuleFormProps): JSX.Element {
  const [resetKey, setResetKey] = useState(0)

  const fieldOptions = fields.map((f) => ({ label: f.label, value: f.id }))

  const defaultValues: AddRuleFormData = {
    whenFieldDefinitionId: '',
    whenValueId: '',
    thenFieldDefinitionId: '',
    thenValueIds: [],
  }

  async function handleSubmit(data: AddRuleFormData): Promise<void> {
    await onSave(data)
    setResetKey((k) => k + 1)
  }

  return (
    <div
      className="mt-4 rounded border border-gray-200 bg-gray-50 p-4"
      data-testid="add-rule-form"
    >
      <h3 className="mb-3 text-sm font-semibold text-gray-700">Add Rule</h3>
      <ZodForm
        key={resetKey}
        schema={AddRuleSchema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
      >
        <AddRuleFormBody
          fieldOptions={fieldOptions}
          valuesByField={valuesByField}
          onCancel={onCancel}
        />
      </ZodForm>
    </div>
  )
}

// ---------------------------------------------------------------------------
// CustomFieldDependenciesEditor — main component
// ---------------------------------------------------------------------------

export default function CustomFieldDependenciesEditor({
  rules,
  fields,
  valuesByField,
  onCreate,
  onDelete,
}: CustomFieldDependenciesEditorProps): JSX.Element {
  const [showForm, setShowForm] = useState(false)
  const toast = useToast()

  const groups = groupByWhenField(rules)

  async function handleCreate(data: AddRuleFormData): Promise<void> {
    await onCreate({
      whenFieldDefinitionId: data.whenFieldDefinitionId,
      whenValueId: data.whenValueId,
      thenFieldDefinitionId: data.thenFieldDefinitionId,
      thenValueIds: data.thenValueIds,
    })
    setShowForm(false)
  }

  async function handleDelete(id: string): Promise<void> {
    try {
      await onDelete(id)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error('Delete failed', msg)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Rules list grouped by when-field */}
      {rules.length === 0 ? (
        <p className="text-sm text-gray-500">No dependency rules configured.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map(({ fieldId, rules: groupRules }) => {
            const whenFieldLabel = labelFor(fields, fieldId)
            return (
              <section key={fieldId} aria-label={`Rules for ${whenFieldLabel}`}>
                <h3 className="mb-1 text-sm font-semibold text-gray-700">{whenFieldLabel}</h3>
                <ul className="flex flex-col gap-1 list-none p-0">
                  {groupRules.map((rule) => {
                    const whenValueLabel = labelFor(
                      valuesByField[rule.whenFieldDefinitionId] ?? [],
                      rule.whenValueId,
                    )
                    const thenFieldLabel = labelFor(fields, rule.thenFieldDefinitionId)
                    const thenValueLabels = rule.thenValueIds
                      .map((vid) =>
                        labelFor(valuesByField[rule.thenFieldDefinitionId] ?? [], vid),
                      )
                      .join(', ')

                    return (
                      <li
                        key={rule.id}
                        className="flex items-center justify-between rounded border border-gray-200 bg-white px-3 py-2"
                      >
                        <span className="text-sm text-gray-800">
                          When <strong>{whenValueLabel}</strong> → {thenFieldLabel}:{' '}
                          <span className="text-gray-600">{thenValueLabels}</span>
                        </span>
                        <button
                          type="button"
                          aria-label="Delete rule"
                          className="ml-4 rounded border border-gray-200 px-2 py-0.5 text-xs text-red-500 hover:border-red-300 hover:text-red-700"
                          onClick={() => void handleDelete(rule.id)}
                        >
                          Delete
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </section>
            )
          })}
        </div>
      )}

      {/* Add rule affordance */}
      {!showForm && (
        <button
          type="button"
          aria-label="Add rule"
          className="self-start rounded border border-dashed border-gray-300 px-3 py-1.5 text-sm text-gray-500 hover:border-gray-400 hover:text-gray-700"
          onClick={() => setShowForm(true)}
        >
          + Add rule
        </button>
      )}

      {showForm && (
        <AddRuleForm
          fields={fields}
          valuesByField={valuesByField}
          onSave={handleCreate}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  )
}
